import React, { useState, useRef, useEffect } from 'react';
import { agentApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

interface Message {
  role: 'user' | 'assistant';
  text: string;
  isError?: boolean;
}

const SUGGESTIONS: string[] = [
  'What is my nexus status across all states?',
  'Which states am I approaching the threshold in?',
  'What is the tax rate for ZIP code 10001?',
  'Run an alert check and tell me the results.',
  'Explain the sourcing rules for California.',
  'What are the nexus thresholds for Texas and Florida?',
];

function AvatarIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 8v4l3 3"/>
    </svg>
  );
}

function FormattedText({ text, isUser }: { text: string; isUser: boolean }): JSX.Element {
  return (
    <div style={{ lineHeight: 1.65 }}>
      {text.split('\n').map((line, i, arr) => (
        <span key={i}>
          {line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) return <strong key={j}>{part.slice(2, -2)}</strong>;
            if (part.startsWith('`') && part.endsWith('`')) return (
              <code key={j} style={{ background: isUser ? 'rgba(255,255,255,0.2)' : '#F0F2F8', padding: '1px 5px', borderRadius: 3, fontSize: '0.9em', fontFamily: 'monospace' }}>
                {part.slice(1, -1)}
              </code>
            );
            return part;
          })}
          {i < arr.length - 1 && <br />}
        </span>
      ))}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  page:       { padding: '18px 22px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', background: '#F8FAFC' },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexShrink: 0 },
  chatOuter:  { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 },
  chatBox:    { flex: 1, overflowY: 'auto', background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px 12px 0 0', padding: '20px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 40 },
  emptyIcon:  { width: 68, height: 68, borderRadius: '50%', background: 'rgba(0,196,204,0.08)', border: '2px solid rgba(0,196,204,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 },
  chip:       { padding: '7px 14px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 20, fontSize: 12, color: '#334155', cursor: 'pointer', fontWeight: 500 },
  msgRow:     { display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 14 },
  avatar:     { width: 26, height: 26, borderRadius: '50%', background: '#00A0A8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  bubble:     { maxWidth: '72%', padding: '10px 14px', borderRadius: 14, fontSize: 13.5, lineHeight: 1.6, wordBreak: 'break-word' },
  bubbleUser: { background: '#1B4F8C', color: '#fff', borderBottomRightRadius: 4 },
  bubbleBot:  { background: '#F4F6FB', color: '#1A1A2E', border: '1px solid #E8EAF0', borderBottomLeftRadius: 4 },
  bubbleError:{ background: '#FFF5F5', borderColor: '#FECACA', color: '#B91C1C' },
  inputBar:   { display: 'flex', alignItems: 'flex-end', gap: 8, background: '#fff', border: '1px solid #E2E8F0', borderTop: 'none', borderRadius: '0 0 12px 12px', padding: '10px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  textarea:   { flex: 1, border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 12px', fontSize: 13.5, fontFamily: 'inherit', resize: 'none', outline: 'none', color: '#0F172A', background: '#F9FAFF', lineHeight: 1.5, maxHeight: 120, overflowY: 'auto' },
  sendBtn:    { width: 38, height: 38, borderRadius: 8, background: '#00A0A8', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
};

export default function AgentChat(): JSX.Element {
  const { organizationId }        = useAuth();
  const [messages, setMessages]   = useState<Message[]>([]);
  const [input,    setInput]      = useState('');
  const [loading,  setLoading]    = useState(false);
  const bottomRef                 = useRef<HTMLDivElement>(null);
  const inputRef                  = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  async function sendMessage(text: string): Promise<void> {
    const question = text.trim();
    if (!question || loading) return;
    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setInput('');
    setLoading(true);
    try {
      const data = await agentApi.chat(question, organizationId);
      setMessages(prev => [...prev, { role: 'assistant', text: data.answer ?? 'No response.' }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Request failed';
      setMessages(prev => [...prev, { role: 'assistant', text: `Error: ${msg}`, isError: true }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#0F172A', letterSpacing: '-.02em' }}>AI Assistant</div>
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
            Powered by Claude · Ask anything about nexus, rates, or filings
          </div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 20, background: 'rgba(0,196,204,0.1)', color: '#00A0A8', border: '1px solid rgba(0,196,204,0.25)' }}>
          claude-opus-4-7
        </span>
      </div>

      <div style={S.chatOuter}>
        <div style={S.chatBox}>
          {messages.length === 0 && !loading && (
            <div style={S.emptyState}>
              <div style={S.emptyIcon}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#67E8F9" strokeWidth="1.5">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0F172A', marginBottom: 6 }}>Ask STAC Assistant</div>
              <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 24, textAlign: 'center', maxWidth: 380, lineHeight: 1.6 }}>
                Get instant answers about nexus thresholds, tax rates, and registration requirements.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 620 }}>
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => sendMessage(s)} style={S.chip}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} style={{ ...S.msgRow, justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              {msg.role === 'assistant' && <div style={S.avatar}><AvatarIcon /></div>}
              <div style={{ ...S.bubble, ...(msg.role === 'user' ? S.bubbleUser : S.bubbleBot), ...(msg.isError ? S.bubbleError : {}) }}>
                <FormattedText text={msg.text} isUser={msg.role === 'user'} />
              </div>
              {msg.role === 'user' && (
                <div style={{ ...S.avatar, background: '#1B4F8C' }}>
                  <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>N</span>
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div style={{ ...S.msgRow, justifyContent: 'flex-start' }}>
              <div style={S.avatar}><AvatarIcon /></div>
              <div style={{ ...S.bubble, ...S.bubbleBot, display: 'flex', alignItems: 'center', gap: 4, padding: '14px 18px' }}>
                {[0, 0.2, 0.4].map((d, i) => (
                  <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: '#94A3B8', display: 'inline-block', animation: `bounce 1s ${d}s infinite` }} />
                ))}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div style={S.inputBar}>
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about nexus status, tax rates, sourcing rules…"
            disabled={loading}
            style={S.textarea}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            style={{ ...S.sendBtn, opacity: !input.trim() || loading ? 0.4 : 1 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
        <div style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', padding: '4px 0 0' }}>
          Enter to send · Shift+Enter for new line
        </div>
      </div>

      <style>{`@keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }`}</style>
    </div>
  );
}
